import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db, isFirebaseConfigured } from '../../firebase/config';
import { UniversalCard } from '../Common/UniversalCard';
import ProgressLoader from '../Common/ProgressLoader';
import './HomePage.css';
import SEOHead from '../SEO/SEOHead';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  authorId: string;
  category: string;
  categorySlug: string;
  featuredImage?: string;
  tags: string[];
  publishedAt: Date;
  updatedAt: Date;
  featured: boolean;
  popular: boolean;
  views: number;
  affiliateLink?: string;
}

const HomePage: React.FC = () => {
  const DEFAULT_DESCRIPTION = 'Discover amazing products with honest reviews, detailed guides, and trusted recommendations to enhance your lifestyle.';
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [popularPosts, setPopularPosts] = useState<BlogPost[]>([]);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    console.log('fetchPosts called - isFirebaseConfigured:', isFirebaseConfigured, 'db:', !!db);
    
    // If Firebase is not configured, show empty state
    if (!isFirebaseConfigured || !db) {
      console.log('Firebase not configured - showing empty state');
      setFeaturedPosts([]);
      setPopularPosts([]);
      setRecentPosts([]);
      setLoading(false);
      return;
    }

    console.log('Firebase is configured, fetching real posts...');
    try {
      // Fetch all posts first to see what we have
      const allPostsQuery = query(
        collection(db, 'posts'),
        orderBy('updatedAt', 'desc')
      );
      const allPostsSnapshot = await getDocs(allPostsQuery);
      console.log('Total posts found in Firebase:', allPostsSnapshot.docs.length);
      
      const allPosts = allPostsSnapshot.docs.map(doc => {
        const data = doc.data();
        const publishedAt = data.publishedAt?.toDate() || data.updatedAt?.toDate() || new Date();
        const updatedAt = data.updatedAt?.toDate() || new Date();
        
        console.log('🖼️ Post image debug:', { 
          id: doc.id, 
          title: data.title, 
          featuredImage: data.featuredImage,
          hasImage: !!data.featuredImage,
          imageType: typeof data.featuredImage
        });
        console.log('Post data:', { 
          id: doc.id, 
          title: data.title, 
          featured: data.featured, 
          views: data.views, 
          publishedAt: publishedAt,
          updatedAt: updatedAt,
          featuredImage: data.featuredImage
        });
        
        return {
          id: doc.id,
          ...data,
          publishedAt: publishedAt,
          updatedAt: updatedAt
        };
      }) as BlogPost[];

      // Sort all posts by updatedAt (newest first) to ensure newly created posts appear first
      const sortedPosts = allPosts.sort((a, b) => {
        const dateA = a.updatedAt.getTime();
        const dateB = b.updatedAt.getTime();
        return dateB - dateA; // Newest first
      });
      console.log('Posts sorted by updatedAt:', sortedPosts.map(p => ({ id: p.id, title: p.title, updatedAt: p.updatedAt })));

      // Fetch featured posts (newest featured posts first)
      const featured = sortedPosts
        .filter(post => post.featured === true)
        .slice(0, 3);
      console.log('Featured posts found:', featured.length, featured.map(p => p.title));

      // Fetch popular posts (by views, but maintain date order for ties)
      const popular = sortedPosts
        .sort((a, b) => {
          const viewDiff = (b.views || 0) - (a.views || 0);
          if (viewDiff === 0) {
            // If same views, sort by date (newest first)
            return b.publishedAt.getTime() - a.publishedAt.getTime();
          }
          return viewDiff;
        })
        .slice(0, 6);
      console.log('Popular posts found:', popular.length, popular.map(p => ({ title: p.title, views: p.views })));

      // Fetch recent posts (most recent 3 by publication date)
      const recent = allPosts
        .sort((a, b) => {
          const dateA = a.publishedAt.getTime();
          const dateB = b.publishedAt.getTime();
          return dateB - dateA; // Newest published first
        })
        .slice(0, 3);
      console.log('Recent posts found:', recent.length, recent.map(p => ({ title: p.title, publishedAt: p.publishedAt })));

      // Set the posts from Firebase data (empty arrays if no posts found)
      setFeaturedPosts(featured);
      setPopularPosts(popular);
      setRecentPosts(recent);
    } catch (error) {
      console.error('Error fetching posts:', error);
      // Show empty state on error
      setFeaturedPosts([]);
      setPopularPosts([]);
      setRecentPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Removed dummy posts - only using real Firebase data

  if (loading) {
    return (
      <ProgressLoader 
        message="Loading Home"
        subtitle="Fetching latest posts and content..."
        fullScreen={true}
      />
    );
  }

  return (
    <div className="nb-home-page">
      <SEOHead
        title=""
        description={DEFAULT_DESCRIPTION}
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://nortiq.ca/'}
        type="website"
        structuredData={JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": (typeof window !== 'undefined' ? window.location.origin : 'https://nortiq.ca') + '/' }
          ]
        })}
      />
      {/* Hero Section */}
      <section className="nb-hero-section">
        <div className="nb-container">
          <div className="nb-hero-content">
            <h1>Discover Amazing Products</h1>
            <p>Your trusted source for honest reviews, detailed guides, and the best product recommendations to enhance your lifestyle.</p>
            <div className="nb-hero-actions">
              <Link to="/recent" className="nb-btn nb-btn-primary">
                Explore Latest Posts
              </Link>
              <Link to="/categories" className="nb-btn nb-btn-secondary">
                Browse Categories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Posts */}
      <section className="nb-section">
        <div className="nb-container">
          <div className="nb-section-header">
            <h2>Recent Posts</h2>
            <p>Latest articles and product reviews</p>
          </div>
          
          <div className="nb-universal-grid nb-universal-grid-3">
            {recentPosts.map((post) => (
              <UniversalCard 
                key={post.id} 
                post={post} 
              />
            ))}
          </div>
          
          <div className="nb-section-footer">
            <Link to="/recent" className="nb-btn nb-btn-primary">
              View All Recent Posts
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="nb-section nb-section-alt">
          <div className="nb-container">
            <div className="nb-section-header">
              <h2>Featured Posts</h2>
              <p>Hand-picked articles showcasing the best products and insights</p>
            </div>
            
            <div className="nb-universal-grid nb-universal-grid-3">
              {featuredPosts.map((post, index) => (
                <UniversalCard 
                  key={post.id} 
                  post={post} 
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular Posts */}
      <section className="nb-section">
        <div className="nb-container">
          <div className="nb-section-header">
            <h2>Popular Posts</h2>
            <p>Most viewed and trending articles from our community</p>
          </div>
          
          <div className="nb-universal-grid nb-universal-grid-3">
            {popularPosts.map((post) => (
              <UniversalCard 
                key={post.id} 
                post={post} 
              />
            ))}
          </div>
          
          <div className="nb-section-footer">
            <Link to="/popular" className="nb-btn nb-btn-secondary">
              View All Popular Posts
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
